import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMovieDetails, getImageUrl, getBackdropUrl } from '../services/tmdb';
import { useStore } from '../store/useStore';
import { loadUserProfile, addFavoriteMovie } from '../data/userPreferences';
import MediaRow from '../components/media/MediaRow';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ChimeraLens from '../components/chimera/ChimeraLens';
import WhereToWatch from '../components/media/WhereToWatch';
import CastCarousel from '../components/media/CastCarousel';
import { getChimeraScan, getEnhancedChimeraScan, type ChimeraScan } from '../services/chimeraLens';
import type { Movie as MovieType } from '../types';

export default function Movie() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [movie, setMovie] = useState<MovieType | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [chimeraScan, setChimeraScan] = useState<ChimeraScan | null>(null);

  const {
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    setTrailerKey
  } = useStore();

  const inWatchlist = movie ? isInWatchlist('movie', movie.id) : false;

  // Check if movie is in favorites
  useEffect(() => {
    if (movie) {
      const profile = loadUserProfile();
      const found = profile.favorites.movies.some(m => m.tmdbId === movie.id || m.title === movie.title);
      setIsFavorite(found);
    }
  }, [movie]);

  const handleFavorite = () => {
    if (!movie) return;
    const year = new Date(movie.release_date).getFullYear();
    addFavoriteMovie({
      title: movie.title,
      tmdbId: movie.id,
      year,
      addedAt: Date.now(),
    });
    setIsFavorite(true);
  };

  useEffect(() => {
    const fetchMovie = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setImageLoaded(false);
        const data = await getMovieDetails(parseInt(id));
        setMovie(data);
      } catch (error) {
        console.error('Error fetching movie:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMovie();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!movie) return;
    const previous = document.title;
    document.title = `${movie.title} — CineVault`;
    return () => {
      document.title = previous;
    };
  }, [movie]);

  useEffect(() => {
    if (!movie) return;

    const fallback = getChimeraScan(movie, 'movie');
    setChimeraScan(fallback);

    let cancelled = false;
    getEnhancedChimeraScan(movie, 'movie').then((scan) => {
      if (!cancelled) setChimeraScan(scan);
    });

    return () => {
      cancelled = true;
    };
  }, [movie]);

  const handleWatchlist = () => {
    if (!movie) return;
    if (inWatchlist) {
      removeFromWatchlist('movie', movie.id);
    } else {
      addToWatchlist({
        id: movie.id,
        mediaType: 'movie',
        title: movie.title,
        posterPath: movie.poster_path,
        backdropPath: movie.backdrop_path,
        voteAverage: movie.vote_average,
        releaseDate: movie.release_date,
        addedAt: Date.now(),
      });
    }
  };

  const trailerKey = movie?.videos?.results?.find(
    (v) => v.type === 'Trailer' && v.site === 'YouTube'
  )?.key;

  if (loading) {
    return <LoadingSkeleton type="detail" />;
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Movie not found</h1>
          <button
            onClick={() => navigate('/')}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const year = new Date(movie.release_date).getFullYear();
  const runtime = movie.runtime ? `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m` : '';
  const genres = movie.genres?.map((g) => g.name).join(', ') || '';

  return (
    <div className="min-h-screen">
      {/* Backdrop */}
      <div className="relative h-[60vh] sm:h-[70vh]">
        <div className="absolute inset-0">
          <img
            src={getBackdropUrl(movie.backdrop_path, 'original')}
            alt={movie.title}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && <div className="absolute inset-0 shimmer" />}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141414] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative -mt-48 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0 w-48 sm:w-64 mx-auto md:mx-0">
            <img
              src={getImageUrl(movie.poster_path, 'w500')}
              alt={movie.title}
              className="w-full rounded-lg shadow-2xl"
            />
          </div>

          {/* Details */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="font-display text-detail-title font-bold mb-2">
              {movie.title}
            </h1>

            {movie.tagline && (
              <p className="text-gray-400 italic text-detail-tagline mb-4">"{movie.tagline}"</p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-detail-meta mb-6">
              <span className="flex items-center gap-1 text-yellow-400 font-semibold">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {movie.vote_average.toFixed(1)} ({movie.vote_count.toLocaleString()} votes)
              </span>
              <span className="text-gray-300">{year}</span>
              {runtime && <span className="text-gray-300">{runtime}</span>}
              <span className="px-2 py-0.5 bg-white/20 rounded text-xs uppercase">Movie</span>
            </div>

            {/* Genres */}
            {genres && (
              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-6">
                {movie.genres?.map((genre) => (
                  <span
                    key={genre.id}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm"
                  >
                    {genre.name}
                  </span>
                ))}
              </div>
            )}

            {/* Overview */}
            <p className="text-gray-300 text-detail-body leading-relaxed mb-8 max-w-3xl">
              {movie.overview}
            </p>

            {chimeraScan && movie && <ChimeraLens scan={chimeraScan} mediaType="movie" mediaId={movie.id} />}

            <WhereToWatch mediaType="movie" mediaId={movie.id} />

            {/* Actions */}
            <div className="mt-8 flex flex-wrap items-center justify-center md:justify-start gap-4">
              <button
                onClick={() => navigate(`/watch/movie/${movie.id}`)}
                className="btn-primary flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
                Watch Now
              </button>

              {trailerKey && (
                <button
                  onClick={() => setTrailerKey(trailerKey)}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Trailer
                </button>
              )}

              {movie.external_ids?.imdb_id && (
                <a
                  href={`https://www.imdb.com/title/${movie.external_ids.imdb_id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border border-yellow-400/30 bg-[#f5c518] px-5 py-3 font-bold text-black transition hover:-translate-y-0.5 hover:bg-[#ffd84d]"
                  aria-label={`Open ${movie.title} on IMDb`}
                >
                  <span className="rounded bg-black px-1.5 py-0.5 text-xs font-black text-[#f5c518]">IMDb</span>
                  Title page ↗
                </a>
              )}

              <button
                onClick={handleWatchlist}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  inWatchlist
                    ? 'bg-white text-black hover:bg-gray-200'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                {inWatchlist ? (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    In My List
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add to List
                  </>
                )}
              </button>

              <button
                onClick={handleFavorite}
                disabled={isFavorite}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isFavorite
                    ? 'bg-red-500 text-white cursor-default'
                    : 'bg-white/20 hover:bg-red-500'
                }`}
                title={isFavorite ? 'Already in your favorites!' : 'Mark as watched & loved'}
              >
                <svg className="w-5 h-5" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {isFavorite ? 'Favorited' : 'Favorite'}
              </button>
            </div>
          </div>
        </div>

        {movie.credits?.cast && <CastCarousel cast={movie.credits.cast} />}

        {/* Recommendations */}
        {movie.recommendations?.results && movie.recommendations.results.length > 0 && (
          <div className="mt-8">
            <MediaRow
              title="You Might Also Like"
              items={movie.recommendations.results.slice(0, 20)}
              type="movie"

            />
          </div>
        )}
      </div>
    </div>
  );
}
